import { useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress, CssBaseline, ThemeProvider } from "@mui/material";
import theme from "./theme";
import { AuthProvider, useAuth0 } from "./hooks/useAuth0";
import { initializeApi } from "./services/api";
import { CartProvider } from "./components/CartContext";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";
import Hub from "./pages/Hub";
import HowItWorks from "./pages/HowItWorks";
import Shop, { ProductDetail } from "./pages/Shop";
import { TestingCompare, TestingDetail, TestingList } from "./pages/Testing";
import { CareAudience, GetCare, InfoPage, MyVisits, TreatmentDetail, Visit } from "./pages/Care";
import Membership from "./pages/Membership";
import Telehealth from "./pages/Telehealth";
import Learn from "./pages/Learn";
import Faqs from "./pages/Faqs";
import Contact from "./pages/Contact";
import Simple from "./pages/Simple";
import Cart from "./pages/Cart";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import Unauthorized from "./pages/Unauthorized";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import Dashboard from "./pages/Dashboard";
import VitaRelay from "./pages/VitaRelay";

// Pages that start with the big gradient panel pass hero=true (default). Others get top spacing for the floating menu.
const wrap = (el, hero = true) => <Layout hero={hero}>{el}</Layout>;

function AppRoutes() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) initializeApi(getAccessTokenSilently);
  }, [isAuthenticated, getAccessTokenSilently]);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Home and audience hubs */}
      <Route path="/" element={wrap(<Home />)} />
      <Route path="/women" element={wrap(<Hub audience="women" />)} />
      <Route path="/men" element={wrap(<Hub audience="men" />)} />

      {/* Shop */}
      <Route path="/shop" element={wrap(<Shop />)} />
      <Route path="/shop/p/:id" element={wrap(<ProductDetail />)} />
      <Route path="/shop/:audience" element={wrap(<Shop />)} />
      <Route path="/cart" element={wrap(<Cart />, false)} />

      {/* Testing */}
      <Route path="/testing" element={wrap(<TestingList />)} />
      <Route path="/testing/compare" element={wrap(<TestingCompare />)} />
      <Route path="/testing/sample-reports" element={wrap(<Dashboard sample />, false)} />
      <Route path="/testing/:slug" element={wrap(<TestingDetail />)} />

      {/* Get Care */}
      <Route path="/get-care" element={wrap(<GetCare />)} />
      <Route path="/get-care/info/:topic" element={wrap(<InfoPage />)} />
      <Route path="/get-care/:audience" element={wrap(<CareAudience />)} />
      <Route path="/treatment/:slug" element={wrap(<TreatmentDetail />)} />
      <Route path="/visit/:slug" element={wrap(<Visit />, false)} />
      <Route path="/visits" element={<ProtectedRoute>{wrap(<MyVisits />, false)}</ProtectedRoute>} />

      <Route path="/exclusive-picks" element={wrap(
        <Simple eyebrow="Exclusive picks" title="Exclusive picks"
          subtitle="Coming soon."
          body={["Placeholder page. The curated exclusive-picks collection will be built here.",
                 "For now you can browse every product in the shop."]}
          cta={{ label: "Shop all products", to: "/shop" }} />)} />
      <Route path="/telehealth" element={wrap(<Telehealth />)} />

      {/* Membership and learning */}
      <Route path="/membership" element={wrap(<Membership />)} />
      <Route path="/learn" element={wrap(<Learn />)} />
      <Route path="/learn/:type" element={wrap(<Learn />)} />

      {/* Company and help */}
      <Route path="/how-it-works" element={wrap(<HowItWorks />)} />
      <Route path="/faqs" element={wrap(<Faqs />)} />
      <Route path="/contact" element={wrap(<Contact />)} />
      <Route path="/about" element={wrap(
        <Simple eyebrow="About VEYSO" title="Health information you can act on"
          subtitle="Sample page. Replace with VEYSO's own story."
          body={["VEYSO brings testing, products and care together so people can see their health clearly and act on it.",
                 "This preview uses sample content. The mission, the VEYSO Standard, experts and provider network will be added once approved by VEYSO."]} />)} />
      <Route path="/employers" element={wrap(
        <Simple eyebrow="For employers" title="Health testing for your team"
          subtitle="Coming soon." body={["Group memberships for employers are planned for a later release."]} />)} />
      <Route path="/privacy" element={wrap(
        <Simple eyebrow="Legal" title="Privacy" subtitle="Placeholder." body={["VEYSO's privacy policy will be provided by VEYSO and reviewed by its counsel before launch."]} />)} />
      <Route path="/terms" element={wrap(
        <Simple eyebrow="Legal" title="Terms" subtitle="Placeholder." body={["VEYSO's terms of service, shipping and returns, and disclaimers will be provided by VEYSO and reviewed by its counsel before launch."]} />)} />

      {/* Old addresses */}
      <Route path="/pricing" element={<Navigate to="/membership" replace />} />
      <Route path="/what-we-test" element={<Navigate to="/testing" replace />} />
      <Route path="/imaging" element={<Navigate to="/testing" replace />} />
      <Route path="/sample-dashboard" element={<Navigate to="/testing/sample-reports" replace />} />

      {/* Auth */}
      <Route path="/login" element={wrap(<Login />, false)} />
      <Route path="/signup" element={wrap(<Signup />, false)} />
      <Route path="/forgot-password" element={wrap(<ForgotPassword />, false)} />
      <Route path="/unauthorized" element={wrap(<Unauthorized />, false)} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Signed-in area */}
      <Route path="/dashboard" element={<ProtectedRoute>{wrap(<Dashboard />, false)}</ProtectedRoute>} />

      {/* Admin only: VitaRelay Rx catalog and orders */}
      <Route path="/admin/rx" element={<ProtectedRoute requiredRoles={["Admin"]}>{wrap(<VitaRelay />, false)}</ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <AppRoutes />
          </CartProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}
